import type { UserAccount, UserRole } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { displayAccountStatus } from '@/entities/user';
import { formatDateTime } from '@/shared/lib';
import { Button, cn, StatusBadge, tableStyles } from '@/shared/ui';

interface UserRowProps {
  /** 계약 타입 그대로 받는다 — 구조 재정의는 계약 드리프트를 조용히 숨긴다. */
  readonly user: UserAccount;
  /** 관리자 계정·자기 자신·탈퇴자는 차단 대상이 아니다. (서버도 거부한다) */
  readonly canModerate: boolean;
  readonly isModerationPending: boolean;
  readonly onBlockRequest: (userId: string) => void;
  readonly onUnblock: (userId: string) => void;
}

const ROLE_LABELS: Record<UserRole, string> = { USER: '회원', ADMIN: '관리자' };

/** 사용자 목록의 한 행 — 계정 요약과 차단·해제 액션. 이메일을 누르면 상세(/users/:id)로 간다. */
export const UserRow = ({
  user,
  canModerate,
  isModerationPending,
  onBlockRequest,
  onUnblock,
}: UserRowProps): ReactElement => {
  const status = displayAccountStatus(user);

  return (
    <tr className={tableStyles.row}>
      <td className={tableStyles.td}>
        <Link
          to={`/users/${user.id}`}
          className="font-medium underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground focus-visible:outline-none"
        >
          {user.email}
        </Link>
      </td>
      <td className={tableStyles.td}>{user.name}</td>
      <td className={cn(tableStyles.td, 'text-muted-foreground')}>{ROLE_LABELS[user.role]}</td>
      <td className={tableStyles.td}>
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </td>
      <td className={cn(tableStyles.td, 'whitespace-nowrap text-muted-foreground')}>
        {formatDateTime(user.createdAt)}
      </td>
      <td className={cn(tableStyles.td, 'text-right')}>
        {!canModerate ? null : user.blockedAt ? (
          <Button
            variant="outline"
            size="sm"
            isLoading={isModerationPending}
            onClick={() => onUnblock(user.id)}
          >
            차단 해제
          </Button>
        ) : (
          <Button variant="destructive" size="sm" onClick={() => onBlockRequest(user.id)}>
            차단
          </Button>
        )}
      </td>
    </tr>
  );
};
